// ═══════════════════════════════════════════════════════════════
// COMPONENT: Company Selector
// Selector de compañía en la barra superior
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useCompany } from '../contexts/CompanyContext';
import { getCompanyLogoUrl } from '../utils/imageUtils';

export default function CompanySelector() {
  const { currentCompany, companies, switchCompany, loading, switchPhase } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const switching = switchPhase !== 'idle';

  // Cerrar dropdown en cuanto inicia la transición
  useEffect(() => {
    if (switching) setIsOpen(false);
  }, [switching]);

  if (loading || !currentCompany) {
    return (
      <div className="px-4 py-2 bg-gray-100 rounded-lg animate-pulse">
        <div className="h-5 w-32 bg-gray-300 rounded"></div>
      </div>
    );
  }

  // Si solo tiene una compañía, mostrar sin dropdown
  if (companies.length === 1) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-lg border-2 border-purple-200">
        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center overflow-hidden">
          {currentCompany.logo ? (
            <img
              src={getCompanyLogoUrl(currentCompany.logo)}
              alt={currentCompany.nombre}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>';
              }}
            />
          ) : (
            <Building2 className="w-5 h-5 text-white" />
          )}
        </div>
        <div>
          <p className="text-sm font-bold text-purple-900">
            {currentCompany.nombre}
          </p>
          {currentCompany.role && (
            <p className="text-xs text-purple-600">
              {getRoleLabel(currentCompany.role)}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Botón selector */}
      <button
        onClick={() => !switching && setIsOpen(!isOpen)}
        disabled={switching}
        className={`flex items-center gap-2 px-4 py-2 bg-white rounded-lg border-2 transition-all shadow-sm ${
          switching
            ? 'border-purple-300 opacity-60 cursor-not-allowed'
            : 'border-gray-200 hover:border-purple-400 hover:shadow-md'
        }`}
      >
        <Building2 className="w-5 h-5 text-purple-600" />
        <div className="text-left">
          <p className="text-sm font-bold text-gray-900">
            {currentCompany.nombre}
          </p>
          {currentCompany.role && (
            <p className="text-xs text-gray-500">
              {getRoleLabel(currentCompany.role)}
            </p>
          )}
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay para cerrar */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute top-full mt-2 right-0 sm:left-0 sm:right-auto w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-white rounded-xl shadow-xl border-2 border-gray-200 z-50 max-h-72 sm:max-h-96 overflow-y-auto">
            <div className="p-2">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                Tus Compañías ({companies.length})
              </p>

              {companies.map(company => (
                <button
                  key={company.id}
                  onClick={() => {
                    switchCompany(company.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-purple-50 transition-all ${
                    company.id === currentCompany.id
                      ? 'bg-purple-100 border-2 border-purple-300'
                      : 'border-2 border-transparent'
                  }`}
                >
                  {/* Logo o icono */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden ${
                    company.id === currentCompany.id
                      ? 'bg-purple-600'
                      : 'bg-gray-200'
                  }`}>
                    {company.logo ? (
                      <img
                        src={getCompanyLogoUrl(company.logo)}
                        alt={company.nombre}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<svg class="w-5 h-5 ${company.id === currentCompany.id ? 'text-white' : 'text-gray-600'}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>`;
                        }}
                      />
                    ) : (
                      <Building2 className={`w-5 h-5 ${
                        company.id === currentCompany.id
                          ? 'text-white'
                          : 'text-gray-600'
                      }`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className={`font-bold truncate ${
                      company.id === currentCompany.id
                        ? 'text-purple-900'
                        : 'text-gray-900'
                    }`}>
                      {company.nombre}
                    </p>
                    {company.nombre_comercial && company.nombre_comercial !== company.nombre && (
                      <p className="text-xs text-gray-500 truncate">
                        {company.nombre_comercial}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {getRoleLabel(company.role)}
                    </p>
                  </div>

                  {/* Check si está seleccionada */}
                  {company.id === currentCompany.id && (
                    <Check className="w-5 h-5 text-purple-600" />
                  )}
                </button>
              ))}
            </div>

            {/* Footer con info */}
            <div className="border-t-2 border-gray-200 p-3 bg-gray-50">
              <p className="text-xs text-gray-600 text-center">
                💡 Al cambiar de compañía se recargarán los datos
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Helper para traducir roles
function getRoleLabel(role) {
  const labels = {
    'super_admin': '👑 Super Admin',
    'company_admin': '🔧 Administrador',
    'supervisor': '📊 Supervisor',
    'rrhh': '👥 RRHH',
    'employee': '👤 Empleado'
  };
  return labels[role] || role;
}
